<!--
  v3.5 (May 12) Inventory Movement History dialog.

  Replaces the broken clock button on the Inventory page that used to
  only log to the console. Shows the last 100 movements for the chosen
  inventory record (same part + warehouse).
-->
<template>
  <v-dialog v-model="visible" max-width="900" scrollable>
    <v-card v-if="item">
      <v-card-title class="d-flex align-center pa-3">
        <v-icon class="me-2" color="primary">mdi-history</v-icon>
        Movement history
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" density="comfortable"
               @click="close" />
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-0">
        <div class="pa-3 text-body-2">
          <strong>{{ item.part_number || ('Part #' + item.part_id) }}</strong>
          <span v-if="item.part_name"> &mdash; {{ item.part_name }}</span>
          <div class="text-caption text-secondary">
            Warehouse: {{ item.warehouse_name || item.warehouse_code }}
            ({{ item.warehouse_code }})
          </div>
        </div>
        <v-divider />
        <div v-if="loading" class="pa-4 text-center">
          <v-progress-circular indeterminate size="22" />
        </div>
        <v-table v-else-if="movements.length" density="compact">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th class="text-right">Qty</th>
              <th class="text-right">Before</th>
              <th class="text-right">After</th>
              <th class="text-right">Unit cost</th>
              <th>Reference</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in movements" :key="m.id">
              <td class="text-no-wrap">{{ formatDate(m.movement_date) }}</td>
              <td>
                <v-chip :color="typeColor(m.movement_type)" size="x-small" variant="tonal">
                  {{ typeLabel(m.movement_type) }}
                </v-chip>
              </td>
              <td class="text-right" :class="m.quantity >= 0 ? 'text-success' : 'text-error'">
                {{ m.quantity > 0 ? '+' : '' }}{{ m.quantity }}
              </td>
              <td class="text-right">{{ m.quantity_before }}</td>
              <td class="text-right"><strong>{{ m.quantity_after }}</strong></td>
              <td class="text-right">
                <span v-if="m.unit_cost">${{ Number(m.unit_cost).toFixed(2) }}</span>
                <span v-else class="text-secondary">&mdash;</span>
              </td>
              <td class="text-caption">
                <span v-if="m.reference_number">{{ m.reference_type }}: {{ m.reference_number }}</span>
                <span v-else class="text-secondary">&mdash;</span>
              </td>
              <td class="text-caption">{{ m.notes || '' }}</td>
            </tr>
          </tbody>
        </v-table>
        <div v-else class="pa-4 text-center text-secondary">
          No movements recorded yet.
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions class="pa-3">
        <span class="text-caption text-secondary">
          Showing last {{ movements.length }} movements.
        </span>
        <v-spacer />
        <v-btn variant="text" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import axios from '@/api/client'
import { useNotify } from '@/composables/useNotify'

const { notify } = useNotify()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  item: { type: Object, default: null },
})
const emit = defineEmits(['update:modelValue'])
const visible = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v),
})

const loading = ref(false)
const movements = ref([])

watch(() => props.modelValue, async v => {
  if (v && props.item) {
    loading.value = true
    movements.value = []
    try {
      const r = await axios.get(`/parts/inventory/${props.item.id}/movements/`)
      movements.value = r.data.movements || []
    } catch (e) {
      console.error('[InventoryMovementsDialog] load failed', e)
      notify.error('Could not load movement history')
    } finally {
      loading.value = false
    }
  }
})

const TYPE_LABELS = {
  receive: 'Receive', issue: 'Issue',
  transfer: 'Transfer',
  transfer_in: 'Transfer in', transfer_out: 'Transfer out',
  adjustment: 'Adjust',
  reserve: 'Reserve', unreserve: 'Unreserve',
  return: 'Return',
}
const TYPE_COLORS = {
  receive: 'success', issue: 'warning',
  transfer_in: 'info', transfer_out: 'info', transfer: 'info',
  adjustment: 'primary',
  reserve: 'amber', unreserve: 'grey',
  return: 'red',
}
function typeLabel(t) { return TYPE_LABELS[t] || t }
function typeColor(t) { return TYPE_COLORS[t] || 'grey' }

function formatDate(s) {
  if (!s) return ''
  try {
    const d = new Date(s)
    return d.toLocaleString(undefined, {
      year: '2-digit', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return s }
}

function close() { visible.value = false }
</script>
